import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PartyPopper, Target, Rocket, Star, GraduationCap, Users, School, Building2, Ticket, Sprout, Store, Dumbbell, Briefcase, Diamond } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import { programs, countries, consents } from "../mock/mock";
import { authApi, guestApi, apiError, COACH_PROGRAMS } from "../lib/api";
import { subscribeToPush } from "../lib/push";
import { usePWA } from "../hooks/usePWA";
import { useLang } from "../i18n/LanguageContext";
import { fmt } from "../i18n/uiText";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ICONS = { PartyPopper, Target, Rocket, Star, GraduationCap, Users, School, Building2, Ticket, Sprout, Store, Dumbbell, Briefcase };
const WHATSAPP_REF = "https://wa.me/message/XD5YLDWYNUHRN1";
const GRUPO_LETRAS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const GRUPO_PROGRAMS = ["mini", "kids", "plus", "high", "pro"];

export default function Register() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { requestNotifications, notify } = usePWA();
  const { u } = useLang();
  const R = u.register;
  const [selected, setSelected] = useState(programId || "kids");
  const [form, setForm] = useState({
    nombre: "", apellidos: "", email: "", password: "", whatsapp: "",
    edad: "12", pais: "México", ciudad: "", colegio: "", grado: "", referencia: "", grupo: "", sexo: "",
    candidato_nombre: "", candidato_programa: "",
  });
  const [hijos, setHijos] = useState([{ referencia: "", nombre: "", email: "" }]);
  const [consent, setConsent] = useState({});

  const active = programs.find((p) => p.id === selected) || programs[1];
  const ActiveIcon = ICONS[active.icon] || Star;
  const isGuest = active.id === "invitados";
  const isParent = active.id === "padres";
  const showGrupo = GRUPO_PROGRAMS.includes(active.id);
  const showSexo = !isGuest && !isParent && (showGrupo || COACH_PROGRAMS.includes(active.name));
  const candPrograms = programs.filter((p) => ["mini", "kids", "plus", "high", "pro"].includes(p.id));
  const setHijo = (i, k, v) => setHijos((arr) => arr.map((h, idx) => idx === i ? { ...h, [k]: v } : h));
  const addHijo = () => setHijos((arr) => [...arr, { referencia: "", nombre: "", email: "" }]);
  const removeHijo = (i) => setHijos((arr) => arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr);

  const [showQuote, setShowQuote] = useState(isGuest);
  const [quote, setQuote] = useState("");

  useEffect(() => {
    if (!isGuest) { setShowQuote(false); return; }
    let alive = true; let timer;
    setShowQuote(true);
    guestApi.content().then((c) => {
      if (!alive) return;
      const quotes = c.quotes || [];
      if (!quotes.length) { setShowQuote(false); return; }
      const idx = parseInt(localStorage.getItem("supera_guest_quote_i") || "0", 10) % quotes.length;
      setQuote(quotes[idx]);
      localStorage.setItem("supera_guest_quote_i", String((idx + 1) % quotes.length));
      timer = setTimeout(() => { if (alive) setShowQuote(false); }, (c.quote_seconds || 7) * 1000);
    }).catch(() => { if (alive) setShowQuote(false); });
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [isGuest]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.password) {
      toast.error(R.errRequired);
      return;
    }
    if (!isGuest && !form.referencia.trim()) {
      toast.error(R.errReference);
      return;
    }
    if (showGrupo && !form.grupo) {
      toast.error("Selecciona tu Grupo Supera.");
      return;
    }
    if (showSexo && !form.sexo) {
      toast.error("Indica si eres hombre o mujer.");
      return;
    }
    if (!isParent && !form.grado.trim()) {
      toast.error("Indica tu grado académico.");
      return;
    }
    if (isParent) {
      if (!form.candidato_nombre.trim() || !form.candidato_programa) {
        toast.error("Indica el nombre y el programa del candidato/a aceptado/a.");
        return;
      }
      if (hijos.some((h) => !h.referencia.trim() || !h.nombre.trim() || !h.email.trim())) {
        toast.error("Completa todos los datos de vinculación de cada candidato/a.");
        return;
      }
    }
    if (!consent.general) {
      toast.error(R.errConsent);
      return;
    }
    try {
      const data = await authApi.register({ ...form, referencia: isGuest ? "" : form.referencia, grupo: showGrupo ? form.grupo : "", sexo: showSexo ? form.sexo : "", programa: active.name, consents: consent, hijos: isParent ? hijos : [] });
      toast.success(fmt(R.welcome, { program: active.name, name: form.nombre }));
      // Best-effort notifications + push subscription — must NOT block navigation.
      requestNotifications()
        .then((perm) => {
          if (perm === "granted") {
            notify("SUPERA", { body: fmt(R.notifBody, { program: active.name, name: form.nombre }) });
            subscribeToPush();
          }
        })
        .catch(() => {});
      const ru = data?.user;
      const dest = ru?.role === "invitado" ? "/invitados"
        : COACH_PROGRAMS.includes(ru?.programa) ? "/coach"
        : "/mi-cuenta";
      navigate(dest);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {showQuote && (
        <div className="fixed inset-0 z-[90] bg-[#0A1C30] flex items-center justify-center p-8 overflow-hidden" data-testid="guest-quote-overlay">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #C9A227 0, transparent 40%), radial-gradient(circle at 80% 80%, #C9A227 0, transparent 40%)" }} />
          <div className="relative max-w-3xl text-center animate-in fade-in zoom-in-95 duration-1000">
            <div className="flex items-center justify-center gap-2 mb-8">
              <Diamond className="w-5 h-5 text-[var(--gold)] fill-[var(--gold)]" />
              <span className="font-extrabold tracking-[0.4em] text-white/80 text-sm">SUPERA</span>
            </div>
            <p className="text-2xl sm:text-4xl lg:text-[2.75rem] font-black text-white leading-[1.2] [text-wrap:balance]">
              {quote}
            </p>
            <div className="mt-10 h-1 w-24 mx-auto rounded-full bg-[var(--gold)]/40 overflow-hidden">
              <div className="h-full bg-[var(--gold)] animate-[grow_7s_linear_forwards]" style={{ width: "100%", transformOrigin: "left", animation: "quoteBar 7s linear forwards" }} />
            </div>
            <button onClick={() => setShowQuote(false)} data-testid="guest-quote-continue"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[var(--gold)] hover:bg-[#d8b53f] text-[#0A1C30] font-extrabold px-8 py-3 text-sm transition-colors">
              Continuar
            </button>
          </div>
          <style>{`@keyframes quoteBar { from { transform: scaleX(0);} to { transform: scaleX(1);} }`}</style>
        </div>
      )}
      <div className="relative bg-navy">
        <Header dark />
        <div className="max-w-3xl mx-auto px-6 pt-24 pb-14">
          <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold transition-colors">
            <ArrowLeft className="w-4 h-4" /> {R.back}
          </button>
          <p className="mt-6 text-xs font-extrabold tracking-[0.25em] text-gold">{R.kicker}</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-extrabold text-white">
            {R.join} <span className="font-serif-it text-gold">SUPERA</span>
          </h1>
          <div className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-white/10 ring-1 ring-[var(--gold)] px-4 py-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: active.bg }}>
              <ActiveIcon className="w-4 h-4" style={{ color: active.color }} />
            </span>
            <span className="font-bold text-white text-sm">{active.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 -mt-6 pb-24">
        <form onSubmit={submit} className="bg-white rounded-[28px] ring-1 ring-black/5 p-7 md:p-10 shadow-sm">
          <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E] mb-6">{R.personalData}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={R.labels.nombre}><Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} placeholder={R.ph.nombre} /></Field>
            <Field label={R.labels.apellidos}><Input value={form.apellidos} onChange={(e) => set("apellidos", e.target.value)} placeholder={R.ph.apellidos} /></Field>
          </div>
          <div className="mt-5"><Field label={R.labels.email}><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={R.ph.email} /></Field></div>
          <div className="mt-5"><Field label={R.labels.password}><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" /></Field></div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={R.labels.whatsapp}><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+52 ..." /></Field>
            <Field label={R.labels.edad}><Input type="number" value={form.edad} onChange={(e) => set("edad", e.target.value)} /></Field>
          </div>
          {showSexo && (
            <div className="mt-5" data-testid="register-sexo">
              <Field label="Soy">
                <div className="grid grid-cols-2 gap-3">
                  {[["hombre", "Hombre (candidato)"], ["mujer", "Mujer (candidata)"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`register-sexo-${v}`} onClick={() => set("sexo", v)}
                      className={`h-11 rounded-xl text-sm font-bold ring-1 transition-colors ${form.sexo === v ? "bg-[#0A1C30] text-white ring-[#0A1C30]" : "bg-white text-[#0A1C30] ring-black/10 hover:ring-[var(--gold)]"}`}>{l}</button>
                  ))}
                </div>
              </Field>
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label={R.labels.pais}>
              <Select value={form.pais} onValueChange={(v) => set("pais", v)}>
                <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label={R.labels.ciudad}><Input value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} placeholder={R.ph.ciudad} /></Field>
          </div>

          <div className={`mt-5 grid grid-cols-1 gap-5 ${isParent ? "" : "md:grid-cols-2"}`}>
            <Field label={R.labels.colegio}><Input value={form.colegio} onChange={(e) => set("colegio", e.target.value)} placeholder={R.ph.colegio} /></Field>
            {!isParent && <Field label="Grado académico"><Input data-testid="register-grado" value={form.grado} onChange={(e) => set("grado", e.target.value)} placeholder="Ej. 5° de primaria, 2° de secundaria" /></Field>}
          </div>

          <div className="mt-5">
            {isGuest ? (
              <div className="rounded-2xl bg-[#F4F6F9] ring-1 ring-black/5 p-4" data-testid="guest-notice">
                <p className="text-sm font-bold text-[#0A1C30]">Registro de invitado</p>
                <p className="mt-1 text-sm text-[#6B7688]">Como invitado no necesitas una referencia Supera. Tendrás acceso al contenido especial habilitado para visitantes.</p>
              </div>
            ) : (
              <>
                <Field label={isParent ? "Referencia Supera (especial para padre o madre Supera)" : R.labels.referencia}>
                  <Input data-testid="register-referencia-input" value={form.referencia} onChange={(e) => set("referencia", e.target.value)} placeholder={R.ph.referencia} />
                </Field>
                <p className="mt-2 text-sm text-[#6B7688]">
                  {R.referenceNotice}{" "}
                  <a href={WHATSAPP_REF} target="_blank" rel="noreferrer" className="font-extrabold text-[#22A860] hover:underline underline-offset-2">
                    {R.referenceHere}
                  </a>
                </p>
                {showGrupo && (
                  <div className="mt-5">
                    <Field label="Grupo Supera">
                      <Select value={form.grupo} onValueChange={(v) => set("grupo", v)}>
                        <SelectTrigger className="h-11 rounded-xl" data-testid="register-grupo-select"><SelectValue placeholder="Selecciona el grupo al que fuiste aceptado/a" /></SelectTrigger>
                        <SelectContent className="max-h-64">
                          {GRUPO_LETRAS.map((g) => <SelectItem key={g} value={g}>Grupo {g}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <p className="mt-2 text-sm text-[#6B7688]">Selecciona el grupo exacto al que fuiste aceptado/a en Supera.</p>
                  </div>
                )}
              </>
            )}
          </div>

          {isParent && (
            <div className="mt-8" data-testid="parent-link-section">
              <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E] mb-4">DATOS DE TU CANDIDATO/A ACEPTADO/A</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Nombre del candidato/a aceptado/a">
                  <Input data-testid="cand-nombre" value={form.candidato_nombre} onChange={(e) => set("candidato_nombre", e.target.value)} placeholder="Nombre completo del hijo/a" />
                </Field>
                <Field label="Programa al que fue aceptado/a">
                  <Select value={form.candidato_programa} onValueChange={(v) => set("candidato_programa", v)}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="cand-programa"><SelectValue placeholder="Selecciona el programa" /></SelectTrigger>
                    <SelectContent>
                      {candPrograms.map((p) => <SelectItem key={p.id} value={p.name}>{p.name} · {p.age}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              <div className="mt-6 rounded-2xl bg-[#F6F3FC] ring-1 ring-[#7C4DDB]/20 p-5">
                <p className="text-sm font-extrabold text-[#5B2E92]">Vincula tu cuenta con la de tu candidato/a aceptado/a</p>
                <p className="text-xs text-[#6B7688] mt-1">Escribe los datos EXACTOS del candidato/a (deben coincidir con su cuenta ya registrada). Puedes vincular a más de un hijo/a.</p>
                {hijos.map((h, i) => (
                  <div key={i} className="mt-4 rounded-xl bg-white ring-1 ring-black/5 p-4" data-testid={`hijo-row-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-extrabold text-[#0A1C30]">Candidato/a {i + 1}</p>
                      {hijos.length > 1 && <button type="button" data-testid={`hijo-remove-${i}`} onClick={() => removeHijo(i)} className="text-xs font-bold text-[#C24141] hover:underline">Quitar</button>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input data-testid={`hijo-ref-${i}`} value={h.referencia} onChange={(e) => setHijo(i, "referencia", e.target.value)} placeholder="Referencia del candidato/a" />
                      <Input data-testid={`hijo-nombre-${i}`} value={h.nombre} onChange={(e) => setHijo(i, "nombre", e.target.value)} placeholder="Nombre del candidato/a" />
                      <Input data-testid={`hijo-email-${i}`} type="email" value={h.email} onChange={(e) => setHijo(i, "email", e.target.value)} placeholder="Correo del candidato/a" />
                    </div>
                  </div>
                ))}
                <button type="button" data-testid="hijo-add" onClick={addHijo} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#7C4DDB] text-white font-bold px-4 py-2 text-sm hover:bg-[#6a3fc0]">+ Añadir otro hijo/a</button>
                <p className="mt-3 text-sm text-[#6B7688]">
                  ¿No tienes estos datos de Supera?{" "}
                  <a href={WHATSAPP_REF} target="_blank" rel="noreferrer" className="font-extrabold text-[#22A860] hover:underline underline-offset-2">Solicítalos AQUÍ</a>
                </p>
              </div>
            </div>
          )}

          <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E] mt-9 mb-4">{R.consentsLabel}</p>
          <div className="divide-y divide-black/5">
            {consents.map((c, i) => (
              <div key={c.id} className="flex items-center justify-between py-3.5">
                <span className="text-sm text-[#2A3446] pr-4">{R.consents[i]}</span>
                <Switch checked={!!consent[c.id]} onCheckedChange={(v) => setConsent((s) => ({ ...s, [c.id]: v }))} />
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl bg-[#FBF3D9] ring-1 ring-[#E9D9A0] p-4 flex items-start gap-3" data-testid="register-review-notice">
            <Star className="w-5 h-5 text-[#B99B2E] flex-shrink-0 mt-0.5" />
            <p className="text-sm font-bold text-[#0A1C30]">Antes de crear tu cuenta, revisa muy bien que todos los datos estén correctos para evitar errores.</p>
          </div>
          <button type="submit" className="mt-4 w-full rounded-2xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold text-lg py-4 transition-colors">
            {R.submit}
          </button>
          <p className="text-center mt-4 text-sm text-[#6B7688]">
            {R.alreadyAccount}{" "}
            <button type="button" onClick={() => navigate("/login")} className="font-bold text-[#0A1C30] hover:text-[#E9A400] transition-colors">{R.loginLink}</button>
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
